import { FC } from 'react';
import TileContainer, { TTileBgOptioons } from './Tile/TileContainer';
import TileHeader from './Tile/TileHeader';
import Link from 'next/link';
import { TEntriesResponse } from '../types/types';
import {
    TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse,
    TypeInsightsWithoutUnresolvableLinksResponse,
} from '../_generated';
import RelatedInsightCard from './Cards/RelatedInsightCard';
import SwiperCarousel from '@/components/Carousel/SwiperCarousel';
import Icon from '@/components/Icon/Icon';
import Button from '@/components/Button/Button';
import { getLocalizedLink } from '@/utils/getLocalizedLink';

type RelatedInsightsCustomTitleProps = {
    title: string;
    seeAllText: string;
    lang: string;
};

type TRelatedInsightsProps = {
    title: string;
    seeAllText: string;
    lang: string;
    insights: TEntriesResponse<TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse[]>;
    readMoreText: string;
    bg?: TTileBgOptioons;
    className?: string;
    flatCards?: boolean;
};

const RelatedInsightsCustomTitle: FC<RelatedInsightsCustomTitleProps> = ({
    title,
    seeAllText,
    lang,
}) => (
    <div className="tw-mb-7 tw-flex tw-gap-5 tw-justify-between tw-items-baseline">
        <h2 className="tw-font-gotham-bold tw-text-textBlue tw-leading-tight tw-text-[26px] md:tw-text-[32px] tw-pb-0 tw-text-center md:tw-text-left tw-grow md:grow-0">
            {title}
        </h2>
        <Link
            href={getLocalizedLink('/insights', lang)}
            className="tw-text-nowrap tw-uppercase hover:tw-no-underline tw-group tw-text-pink1 tw-hidden md:tw-block"
        >
            {seeAllText}
            <Icon
                type="angleRight"
                className="tw-inline-block tw-fill-[#ce0058] tw-w-[20px] tw-h-[20px] tw-pb-[1px] tw-text-[20px] tw-tracking-wide tw-ml-1 group-hover:tw-translate-x-1.5 tw-transition-transform "
            />
        </Link>
    </div>
);

const RelatedInsights: FC<TRelatedInsightsProps> = ({
    title,
    seeAllText,
    lang,
    insights,
    readMoreText,
    bg,
    className,
    flatCards = true,
}) => (
    <TileContainer bg={bg} className={className}>
        <TileHeader
            title={<RelatedInsightsCustomTitle title={title} seeAllText={seeAllText} lang={lang} />}
        />
        <SwiperCarousel
            config={{ multiCard: true, autoplay: true, withArrowButtons: false, type: 'insights' }}
        >
            {insights.items.map((item) => (
                <RelatedInsightCard
                    key={item.sys.id}
                    insight={item}
                    readMoreText={readMoreText}
                    flat={flatCards}
                />
            ))}
        </SwiperCarousel>
        <div className="md:tw-hidden tw-pt-8 tw-flex tw-justify-center">
            <Button
                label={seeAllText}
                path={`/insights`}
                className="tw-block tw-uppercase !tw-text-[14px] !tw-leading-[16px]"
            />
        </div>
    </TileContainer>
);

export default RelatedInsights;
